import { Button, Dropdown, Spin, Table } from "antd"
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"

import { Filter } from "Components/filter/Filter"
import { FilterSection } from "Components/filter/FilterSection"
import { PurchaseOrderFilterForm, PurchaseOrderFilterFormValues } from "Components/forms/filter/PurchaseOrderFilterForm"
import { NoSearchDataFound } from "Components/NoSearchDataFound"
import { PageBody } from "Components/page/pageBody"
import { PageTitle } from "Components/page/pageTitle"
import { SearchInput } from "Components/searchInput"
import { Plus as PlusIcon } from "react-feather"
import { useFilter } from "Shared/hooks/useFilter"
import { convertSortOrder } from "Shared/queries/query.utils"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { PURCHASE_ORDERS_TABLE_FILTERS } from "Shared/services/localstorage.service"
import { GeneratePurchaseOrderColumns, purchaseOrderColumnMapper } from "./columns/PurchaseOrderColumns"
import { DraggableColumnMenu } from "./components/DragableColumnMenu"
import { PurchaseOrderBillingBanner } from "./components/PurchseOrderBillingBanner"
import { PurchaseOrderDrawer } from "./drawer/PurchaseOrderDrawer"
import { useDrawer } from "./hooks/useDrawer.hook"
import { useMutatePurchaseOrders, usePurchaseOrder, usePurchaseOrders } from "./hooks/usePurchaseOrders.hook"
import { usePurchaseOrdersTable } from "./hooks/usePurchaseOrdersTable.hook"
import { useSnapToFirstNonStickyOnResize } from "./hooks/useTableSnapOnResize.hook"
import { BulletPointIcon } from "./icons/BulletPointIcon"
import { PurchaseOrder, PurchaseOrderFormValues, PurchaseOrderTableColumn } from "./types/PurchaseOrder.interfaces"

const usePurchaseOrderFilter = () => useFilter<PurchaseOrderFilterFormValues>({ localStorageKey: PURCHASE_ORDERS_TABLE_FILTERS })

export const PurchaseOrdersListPage = () => {
    const { t } = useTranslation()

    const { filters, filterQuery, onFilter, onRemoveFilter, isFilterModalShowing, showFilterModal, hideFilterModal } = usePurchaseOrderFilter()

    const {
        tableData,
        isLoading,
        handleSearch,
        search,
        isTableEmpty,
        isNoSearchDataFound,
        pagination: { current: currentPage, pageSize, total: totalItems },
        setCurrentPage,
        setPageSize,
        setSortField,
        setSortOption,
        resetSorting,
    } = usePurchaseOrders(filterQuery)

    const { deletePo } = useMutatePurchaseOrders()
    const { getPO } = usePurchaseOrder()

    const { isDrawerOpen, openDrawer, closeDrawer } = useDrawer<PurchaseOrderFormValues>()

    const [editingPO, setEditingPO] = React.useState<PurchaseOrder>()

    const { tableProps, visibleColumns, columnOrder, handleToggleColumn, allColumns, handleReorderColumns } = usePurchaseOrdersTable<PurchaseOrderTableColumn>({
        data: tableData,
        generateColumns: GeneratePurchaseOrderColumns,
        basePath: RoutePathEnum.PURCHASE_ORDER_DETAILS_PAGE,
        // Enable pagination
        enablePagination: true,
        totalItems,
        // Pass custom handlers if needed
        columnFunctions: {
            updateRecord: async (item: PurchaseOrderTableColumn) => {
                // This should trigger opening the modal
                const poData = await getPO(item.key)
                if (poData) {
                    setEditingPO(poData)
                    openDrawer()
                }
            },
            deleteRecord: async (key: string) => {
                await deletePo(key)
            },
            getRecord: (po: PurchaseOrderTableColumn) => getPO(po.key),
            t,
        },
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,

        setSorting: ({ field, order }) => {
            if (field && order) {
                setSortField(purchaseOrderColumnMapper[field as keyof typeof purchaseOrderColumnMapper]!)
                setSortOption(convertSortOrder(order))
            } else {
                resetSorting()
            }
        },
    })

    const scrollRef = useRef<HTMLDivElement>(null)
    useSnapToFirstNonStickyOnResize(scrollRef)

    if (isLoading) {
        return <Spin />
    }

    return (
        <>
            <PurchaseOrderBillingBanner />

            <PageTitle titleKey={"purchaseOrders:list.title"}>
                <SearchInput className="min-w-32" onSearch={handleSearch} value={search} hidden={false} disabled={isTableEmpty} />
                <Filter
                    {...{
                        FilterForm: PurchaseOrderFilterForm,
                        disabled: isTableEmpty,
                        isFilterModalShowing,
                        showFilterModal,
                        hideFilterModal,
                        filters,
                        onFilter,
                    }}
                />
                <Dropdown
                    dropdownRender={() => (
                        <DraggableColumnMenu
                            {...{
                                allColumns,
                                columnOrder,
                                visibleColumns,
                                onToggleColumn: handleToggleColumn,
                                onReorderColumns: handleReorderColumns,
                                i18nColumnBasePath: "purchaseOrders:columns.",
                            }}
                        />
                    )}
                    trigger={["click"]}
                    placement="bottomRight"
                    className="z-50 flex-1"
                    disabled={isTableEmpty}
                >
                    <Button icon={<BulletPointIcon />}>{t("purchaseOrders:list.columns")}</Button>
                </Dropdown>
            </PageTitle>

            <FilterSection filters={filters} onRemove={onRemoveFilter} />

            <PageBody>
                {isTableEmpty ? (
                    <NoSearchDataFound title="info:purchase_order.empty_table.title" message="info:purchase_order.empty_table.text" icon="./icons/empty_table.svg">
                        <Button className="mt-16" type="primary" onClick={openDrawer}>
                            <PlusIcon />
                            <span>{t("info:purchase_order.empty_table.button")}</span>
                        </Button>
                    </NoSearchDataFound>
                ) : isNoSearchDataFound ? (
                    <NoSearchDataFound title="info:no_filter_data_found.title.purchase_order" message="info:no_filter_data_found.message_approver" />
                ) : (
                    <Table ref={scrollRef} {...tableProps} />
                )}
            </PageBody>

            <PurchaseOrderDrawer open={isDrawerOpen} onClose={closeDrawer} editMode={Boolean(editingPO)} purchaseOrder={editingPO} />
        </>
    )
}
